import { motion } from "framer-motion";
import {
  HandHeart, BookOpen, ShieldCheck, Trophy,
  Sparkles, Heart, Scale, HelpingHand,
  Mountain, UsersRound,
} from "lucide-react";

/**
 * Royal English Medium High School — 10 Core Islamic Values.
 * Designed as a premium editorial grid: large numeral, English term first,
 * Arabic transliteration as accent.
 */
const VALUES = [
  { en: "Faith",        ar: "Iman",        Icon: HandHeart,
    desc: "Belief in one God and trust in the divine plan as the foundation of life." },
  { en: "Knowledge",    ar: "Ilm",         Icon: BookOpen,
    desc: "The lifelong pursuit of learning, wisdom and understanding." },
  { en: "Integrity",    ar: "Amanah",      Icon: ShieldCheck,
    desc: "Honesty, trustworthiness and faithfulness in every responsibility." },
  { en: "Excellence",   ar: "Ihsan",       Icon: Trophy,
    desc: "Striving for the highest quality in everything we do." },
  { en: "Respect",      ar: "Adab",        Icon: Sparkles,
    desc: "Good manners, courtesy and dignity toward all people." },
  { en: "Compassion",   ar: "Rahmah",      Icon: Heart,
    desc: "Mercy, kindness and care for every living being." },
  { en: "Justice",      ar: "Adl",         Icon: Scale,
    desc: "Fairness, equity and standing for what is right." },
  { en: "Service",      ar: "Khidmah",     Icon: HelpingHand,
    desc: "Selflessly serving family, community and humanity." },
  { en: "Perseverance", ar: "Sabr",        Icon: Mountain,
    desc: "Patience and resilience through life's challenges." },
  { en: "Unity",        ar: "Ukhuwwah",    Icon: UsersRound,
    desc: "Brotherhood, sisterhood and togetherness as one community." },
];

export default function IslamicValues() {
  return (
    <section
      data-testid="islamic-values"
      className="relative py-24 px-6 overflow-hidden bg-gradient-to-br from-royal-dark via-royal to-royal-dark text-white"
    >
      {/* decorative blobs */}
      <div className="absolute -top-32 -left-32 w-[28rem] h-[28rem] rounded-full bg-gold/15 blur-3xl" />
      <div className="absolute -bottom-32 -right-32 w-[32rem] h-[32rem] rounded-full bg-royal-light/30 blur-3xl" />

      <div className="relative max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto">
          <span className="inline-block bg-white/10 border border-gold/40 text-gold px-5 py-1.5 rounded-full text-xs font-bold tracking-[0.3em]">
            OUR FOUNDATION
          </span>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl font-bold mt-6 leading-tight">
            Our Core <span className="text-gold italic">Islamic</span> Values
          </h2>
          <div className="w-16 h-1 bg-gold mx-auto mt-5 rounded-full" />
          <p className="mt-7 text-lg md:text-xl text-gray-200 leading-relaxed italic">
            "Empowering students with <span className="text-gold not-italic font-semibold">knowledge</span>,
            <span className="text-gold not-italic font-semibold"> character</span> and
            <span className="text-gold not-italic font-semibold"> faith</span> to become responsible
            citizens and compassionate leaders."
          </p>
        </div>

        {/* Grid */}
        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
          {VALUES.map((v, i) => (
            <motion.div
              key={v.en}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.5, delay: (i % 5) * 0.06 }}
              data-testid={`value-${i + 1}`}
              className="group relative rounded-2xl border border-white/15 bg-white/[0.06] hover:bg-white/[0.10] backdrop-blur-sm p-6 transition-all hover:-translate-y-1 hover:border-gold/60"
            >
              {/* Numeral */}
              <span className="absolute top-4 right-4 font-serif-display text-3xl text-gold/30 group-hover:text-gold/70 transition-colors">
                {String(i + 1).padStart(2, "0")}
              </span>

              {/* Icon */}
              <div className="w-12 h-12 rounded-xl bg-gold/15 text-gold flex items-center justify-center mb-5 group-hover:bg-gold group-hover:text-royal-dark transition-colors">
                <v.Icon size={22} />
              </div>

              <h3 className="font-serif-display text-2xl font-bold leading-tight">{v.en}</h3>
              <p className="font-serif-display text-gold italic text-sm tracking-wide mt-0.5">{v.ar}</p>

              <div className="w-8 h-px bg-gold/40 my-4" />

              <p className="text-sm text-gray-300 leading-relaxed">{v.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
